import {usePlusLoadExpected} from '@hconnect/tuc-processes/fields/plusLoad/usePlusLoadExpected'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement'
import {usePlusLoad} from '@hconnect/tuc-processes/orderManagement/orderChange/plusLoad/usePlusLoad'
import {Alert, Kpi} from '@hm/fundamental'
import {Box, Typography, Collapse} from '@mui/material'
import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {trackEvent} from '../../../../../../common/analytics'
import {RadioButtonGroup} from '../../../../../../Molecules/RadioButton'
import {useFieldEditorValidation} from '../../../../shared/order-editor/contexts/FieldEditorValidationContext'
import {QuantityInput} from '../../../../shared/order-editor/components/fields/Quantity'
import {useQuantityCorrection} from '../../../../shared/order-editor/components/fields/Quantity/useQuantityCorrection'

/**
 * Quantity step for orders where plus load is enabled.
 * Allows managing the plus load quantity (additional quantity after last truck loaded).
 */
export const OrderManagementPlusLoadQuantityStep: React.FC = () => {
  const {t} = useTranslation()

  // Use the form context to get validation errors
  const {formState} = useOrderProcessFormContext()

  // Use the plus load hook to get plus load specific data
  const plusLoadData = usePlusLoad()

  // Plus Load Expected flag for "Will you need more plus load(s)?"
  const {setPlusLoad} = usePlusLoadExpected()
  const plusLoadDisabled = useChangeableField('plusLoadExpected').state === 'notChangeable'
  const quantityDisabled = useChangeableField('quantity').state === 'notChangeable'

  // Notify parent FieldEditorView about validity
  const {setFieldValid} = useFieldEditorValidation()

  // Local radio state starts as undefined (no pre-selection)
  const [localRadioValue, setLocalRadioValue] = useState<boolean | undefined>(undefined)

  // On mount: if radio is required (not disabled), the field starts invalid
  useEffect(() => {
    if (!plusLoadDisabled) {
      setFieldValid(false)
    }
    // Only run once on mount — plusLoadDisabled is config, won't change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRadioChange = useCallback(
    (value: boolean) => {
      setLocalRadioValue(value)
      setPlusLoad(value)
      setFieldValid(true)
    },
    [setPlusLoad, setFieldValid]
  )

  // Get validation errors
  const quantityError = (formState.errors?.quantity as {message?: string} | undefined)?.message
  const plusLoadError = formState.errors?.plusLoadExpected?.message

  // Extract range values (with defaults for hook call)
  const range = plusLoadData.isEnabled ? plusLoadData.range : undefined
  const minValue = range?.min ?? 0
  const maxValue = range?.max ?? 0
  const step = range?.step ?? 0.5
  const quantityUom = range?.uom ?? 'M3'

  // Use custom hook for auto-correction management (must be called unconditionally)
  const {correctionMessage, hasCorrection, handleAutoCorrect} = useQuantityCorrection({
    minValue,
    maxValue,
    step,
    quantityUom
  })

  // Plus load should always be enabled when this component is rendered
  if (!plusLoadData.isEnabled || !range) {
    return null
  }

  const {
    plusLoadQuantity = 0,
    setPlusLoadQuantity,
    currentQuantity = 0,
    newTotalQuantity = 0
  } = plusLoadData

  // Type guard for setter
  if (!setPlusLoadQuantity) {
    return null
  }

  // Handle plusload quantity change with analytics tracking
  const handlePlusLoadQuantityChange = (newPlusLoadQuantity: number) => {
    trackEvent('rmcPlusLoadQuantityChanged', {
      plusLoadQuantity: newPlusLoadQuantity,
      unit: quantityUom,
      businessLine: 'RMC'
    })

    void setPlusLoadQuantity(newPlusLoadQuantity)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        padding: 0
      }}
    >
      {/* Plus Load Quantity Details */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '48px', // 12 * 4px = 48px from design
          alignItems: 'center'
        }}
      >
        {/* Plus Load Quantity Input Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px', // 4 * 4px = 16px from design
            width: '100%'
          }}
        >
          <QuantityInput
            value={plusLoadQuantity}
            onChange={handlePlusLoadQuantityChange}
            range={range}
            onAutoCorrect={handleAutoCorrect}
            disabled={quantityDisabled}
            label={t('orderIntake.rmc.orderManagement.quantityChange.plusLoad.label', 'Plus load')}
            data-test-id="rmc-plus-load-quantity-input"
          />

          {/* Quantity validation error display */}
          {quantityError && (
            <Box sx={{mt: 1}}>
              <Typography color="error" variant="body2">
                {quantityError}
              </Typography>
            </Box>
          )}

          {/* Correction notification - show when an auto-correction happens */}
          <Collapse in={hasCorrection}>
            <Alert variant="info" data-test-id="plus-load-quantity-correction-alert">
              {correctionMessage}
            </Alert>
          </Collapse>

          {/* Hint about max request */}
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#6B7280'
            }}
          >
            {t(
              'orderIntake.rmc.orderManagement.quantityChange.plusLoad.maxRequestHint',
              'You can request up to {{max}} {{uom}}.',
              {max: maxValue, uom: quantityUom}
            )}
          </Typography>

          {/* KPI Display */}
          <Box
            sx={{
              display: 'flex',
              gap: '16px', // 4 * 4px = 16px from design
              width: '100%'
            }}
          >
            <Kpi
              className="flex-1"
              size="xsmall"
              label={t(
                'orderIntake.rmc.orderManagement.quantityChange.plusLoad.currentQuantity',
                'Current quantity'
              )}
              value={currentQuantity}
              unit={quantityUom}
            />
            <Kpi
              className="flex-1"
              size="xsmall"
              label={t(
                'orderIntake.rmc.orderManagement.quantityChange.plusLoad.newTotalQuantity',
                'New total quantity'
              )}
              value={newTotalQuantity}
              unit={quantityUom}
            />
          </Box>

          {/* Plus Load Expected Toggle - only visible if not disabled */}
          {!plusLoadDisabled && (
            <Box data-test-id="plus-load-more-section">
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '24px',
                  color: '#29333D',
                  marginBottom: '20px'
                }}
              >
                {t(
                  'orderIntake.rmc.orderManagement.quantityChange.plusLoad.morePlusLoadNeeded',
                  'Will you need more plus load(s)?'
                )}
              </Typography>

              <RadioButtonGroup
                value={localRadioValue}
                onValueChange={handleRadioChange}
                options={[
                  {
                    label: t(
                      'orderIntake.rmc.orderManagement.quantityChange.plusLoad.morePlusLoadNo',
                      'No'
                    ),
                    value: false,
                    testId: 'plus-load-more-toggle-no'
                  },
                  {
                    label: t(
                      'orderIntake.rmc.orderManagement.quantityChange.plusLoad.morePlusLoadYes',
                      'Yes, keep this option open'
                    ),
                    value: true,
                    testId: 'plus-load-more-toggle-yes'
                  }
                ]}
                direction="column"
                spacing={1}
                variant="neutral"
                testId="plus-load-more-radio-group"
              />
            </Box>
          )}

          {/* Plus Load validation error display */}
          {plusLoadError && (
            <Box sx={{mt: 1}}>
              <Typography color="error" variant="body2">
                {plusLoadError}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
